import type { BotLevel, Lang, ModeId, RoundState } from '@polyglot/engine';
import { api, ApiError, ensureSession } from './api';

/**
 * Local practice rounds (solo, robots, relax) are saved to the player's account so that they
 * appear in the history. Played offline, they wait in a small device queue and are sent later.
 * The server replays them; they never count for streaks, badges or leaderboards.
 */
interface PracticeUpload {
  clientId: string;
  mode: ModeId;
  rulesVersion: string;
  languages: Lang[];
  rack: string[];
  seed: string;
  words: { key: string; lang: Lang; atMs: number }[];
  endedAtMs: number;
  bot: { level: BotLevel; languages: Lang[] } | null;
}

const QUEUE_KEY = 'pa.practiceQueue.v1';
const MAX_QUEUE = 50;
let flushing: Promise<void> | null = null;

function readQueue(): PracticeUpload[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as PracticeUpload[];
  } catch {
    return [];
  }
}

function writeQueue(q: PracticeUpload[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE)));
  } catch {
    /* storage unavailable: the round is simply not saved */
  }
}

export function queuePractice(
  session: {
    id: string;
    seed: string;
    rack: string[];
    setup: { mode: ModeId; languages: Lang[]; bot: { level: BotLevel; languages: Lang[] } | null };
  },
  state: RoundState,
): void {
  if (state.status !== 'finished') return;
  const q = readQueue();
  if (q.some((u) => u.clientId === session.id)) return;
  q.push({
    clientId: session.id,
    mode: session.setup.mode,
    rulesVersion: state.config.rulesVersion,
    languages: session.setup.languages,
    rack: session.rack,
    seed: session.seed,
    words: state.words.map((w) => ({ key: w.key, lang: w.lang, atMs: w.atMs })),
    endedAtMs: state.endedAtMs ?? 0,
    bot: session.setup.bot,
  });
  writeQueue(q);
  void flushPractice();
}

/** Sends queued rounds; keeps them on network or server errors, drops rejected ones (4xx). */
export function flushPractice(): Promise<void> {
  if (!flushing) {
    // Reset only after completion (never synchronously), so a later round starts a new flush.
    flushing = sendQueued().finally(() => {
      flushing = null;
    });
  }
  return flushing;
}

async function sendQueued(): Promise<void> {
  try {
    if (readQueue().length === 0) return;
    await ensureSession();
    for (const upload of readQueue()) {
      try {
        await api('POST', '/api/me/practice', upload);
      } catch (e) {
        const retry =
          !(e instanceof ApiError) || e.status === 0 || e.status >= 500 || e.status === 429;
        if (retry) return;
      }
      writeQueue(readQueue().filter((u) => u.clientId !== upload.clientId));
    }
  } catch {
    /* offline or no session yet: retried on the next start or round */
  }
}

export function pendingPractice(): number {
  return readQueue().length;
}
